import { useState } from 'react';
import {
    Grid,
    TextField,
    Button,
    Typography,
    Paper,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
    Box,
    Stack,
    IconButton,
    Avatar,
    Switch
} from '@mui/material';
import { CloudUpload, ArrowBack, Delete, Add, DragIndicator } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// Hooks and Services
import { useCreateProduct } from 'hooks/useProduct';
import { useProductCategoriesForSelection } from 'hooks/useProductCategories';
import productService from 'api/productService';
import { useCurrentVendor } from 'hooks/useUser';
import LocationWarningDialog from '../settings/LocationWarningDialog';

const validationSchema = Yup.object({
    name: Yup.string().required('Product name is required'),
    category: Yup.string().required('Please select a category'),
    price: Yup.number().typeError('Must be a number').positive('Price must be positive').required('Price is required'),
    description: Yup.string().required('Description is required'),
    thumbnail: Yup.string().required('Main thumbnail is required'),
    images: Yup.array().min(1, 'Please upload at least one gallery image'),
    unitOfMeasure: Yup.string().max(15, 'Too long (max 15 chars)').required('Required'),
    available: Yup.boolean(),
    stock: Yup.number().min(0, 'Stock cannot be negative').required('Stock is required'),
    prepTime: Yup.number().typeError('Must be a number').min(0, 'Cannot be negative').required('Preparation time is required')
});

const VendorCreateProduct = () => {
    const navigate = useNavigate();
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    // UI States
    const [uploadingThumb, setUploadingThumb] = useState(false);
    const [uploadingImages, setUploadingImages] = useState(false);

    const standardUnits = ['pieces', 'kg', 'plates', 'liters', 'packs'];

    // Queries & Mutations
    const { mutate: createProduct, isPending: isSubmitting } = useCreateProduct();
    const { data: categoryResponse } = useProductCategoriesForSelection({ limit: 1000 });
    const { data: vendorData, isLoading: loadingVendor } = useCurrentVendor();
    const categories = categoryResponse?.data || [];

    const formik = useFormik({
        initialValues: {
            name: '',
            category: '',
            price: '',
            description: '',
            thumbnail: '',
            images: [],
            stock: 0,
            unitOfMeasure: 'pieces',
            available: true,
            prepTime: 15
        },
        validationSchema,
        onSubmit: (values) => {
            createProduct(values, {
                onSuccess: () => {
                    setToast({ open: true, message: 'Product published!', severity: 'success' });
                    setTimeout(() => navigate('/vendor/products'), 1500);
                },
                onError: (err) => setToast({ open: true, message: err?.response?.data?.message || 'Error', severity: 'error' })
            });
        }
    });

    // --- UPLOAD HANDLERS ---
    const handleThumbnailUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploadingThumb(true);
            const res = await productService.uploadFile(file);
            formik.setFieldValue('thumbnail', res.url);
        } catch (err) {
            setToast({ open: true, message: 'Upload failed', severity: 'error' });
        } finally {
            setUploadingThumb(false);
        }
    };

    const handleImagesUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        try {
            setUploadingImages(true);
            const uploaded = await Promise.all(files.map((f) => productService.uploadFile(f)));
            formik.setFieldValue('images', [...formik.values.images, ...uploaded.map((r) => r.url)]);
        } catch (err) {
            setToast({ open: true, message: 'Gallery upload failed', severity: 'error' });
        } finally {
            setUploadingImages(false);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(formik.values.images);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        formik.setFieldValue('images', items);
    };

    const removeGalleryImage = (index) => {
        const updated = formik.values.images.filter((_, i) => i !== index);
        formik.setFieldValue('images', updated);
    };

    if (loadingVendor) return <CircularProgress />;

    const vendor = vendorData?.vendor;
    if (!vendor?.location || !vendor?.location?.coordinates || vendor?.location?.coordinates?.length !== 2) {
        return <LocationWarningDialog open={true} onClose={() => navigate('/vendor/products')} />;
    }

    return (
        <Box p={3} sx={{ bgcolor: '#fbfbfb', minHeight: '100vh' }}>
            <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast({ ...toast, open: false })}>
                <Alert severity={toast.severity} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>

            <Stack direction="row" spacing={2} mb={4} alignItems="center">
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" fontWeight={800}>
                    Create Product
                </Typography>
            </Stack>

            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    {/* LEFT COLUMN: BASIC INFO */}
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 4, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    General Information
                                </Typography>
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        label="Product Name"
                                        {...formik.getFieldProps('name')}
                                        error={formik.touched.name && !!formik.errors.name}
                                        helperText={formik.touched.name && formik.errors.name}
                                    />

                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Category"
                                            {...formik.getFieldProps('category')}
                                            error={formik.touched.category && !!formik.errors.category}
                                        >
                                            {categories.map((cat) => (
                                                <MenuItem key={cat._id} value={cat._id}>
                                                    {cat.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <IconButton sx={{ mt: 1, bgcolor: 'primary.light', color: 'primary.main' }}>
                                            <Add />
                                        </IconButton>
                                    </Stack>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Price (₦)"
                                                {...formik.getFieldProps('price')}
                                                error={formik.touched.price && !!formik.errors.price}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField fullWidth label="Stock" {...formik.getFieldProps('stock')} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField select fullWidth label="Unit" {...formik.getFieldProps('unitOfMeasure')}>
                                                {standardUnits.map((u) => (
                                                    <MenuItem key={u} value={u}>
                                                        {u}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Prep Time (mins)"
                                                {...formik.getFieldProps('prepTime')}
                                                error={formik.touched.prepTime && !!formik.errors.prepTime}
                                                helperText={formik.touched.prepTime && formik.errors.prepTime}
                                            />
                                        </Grid>
                                    </Grid>

                                    <TextField fullWidth label="Description" multiline rows={4} {...formik.getFieldProps('description')} />
                                </Stack>
                            </Paper>

                            {/* --- GALLERY SECTION (RE-ADDED) --- */}
                            <Paper sx={{ p: 4, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            Product Gallery
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Drag images to reorder. First image is the secondary view.
                                        </Typography>
                                    </Box>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        disabled={uploadingImages}
                                        startIcon={uploadingImages ? <CircularProgress size={16} /> : <Add />}
                                    >
                                        Add Images
                                        <input type="file" multiple hidden accept="image/*" onChange={handleImagesUpload} />
                                    </Button>
                                </Stack>

                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="gallery" direction="horizontal">
                                        {(provided) => (
                                            <Box
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                sx={{
                                                    display: 'flex',
                                                    gap: 2,
                                                    flexWrap: 'wrap',
                                                    minHeight: 120,
                                                    p: 2,
                                                    bgcolor: '#fafafa',
                                                    borderRadius: 2,
                                                    border: '1px dashed #ccc'
                                                }}
                                            >
                                                {formik.values.images.map((img, index) => (
                                                    <Draggable key={img} draggableId={img} index={index}>
                                                        {(provided) => (
                                                            <Box
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                sx={{ position: 'relative', width: 100, height: 100 }}
                                                            >
                                                                <Avatar
                                                                    src={img}
                                                                    variant="rounded"
                                                                    sx={{ width: '100%', height: '100%', border: '1px solid #eee' }}
                                                                />
                                                                <Box
                                                                    {...provided.dragHandleProps}
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 2,
                                                                        left: 2,
                                                                        bgcolor: 'rgba(255,255,255,0.8)',
                                                                        borderRadius: 1,
                                                                        display: 'flex'
                                                                    }}
                                                                >
                                                                    <DragIndicator sx={{ fontSize: 16 }} />
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => removeGalleryImage(index)}
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: -8,
                                                                        right: -8,
                                                                        bgcolor: 'error.main',
                                                                        color: 'white',
                                                                        '&:hover': { bgcolor: 'error.dark' }
                                                                    }}
                                                                >
                                                                    <Delete sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                                {formik.errors.images && formik.touched.images && (
                                    <Typography variant="caption" color="error">
                                        {formik.errors.images}
                                    </Typography>
                                )}
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* RIGHT COLUMN: MEDIA & ACTIONS */}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                                <Typography variant="subtitle2" mb={2}>
                                    Main Thumbnail
                                </Typography>
                                <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fafafa' }}>
                                    {formik.values.thumbnail ? (
                                        <Box>
                                            <Avatar
                                                src={formik.values.thumbnail}
                                                variant="rounded"
                                                sx={{ width: 160, height: 160, mx: 'auto', mb: 1 }}
                                            />
                                            <Button size="small" color="error" onClick={() => formik.setFieldValue('thumbnail', '')}>
                                                Remove
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Button component="label" sx={{ py: 4, display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            {uploadingThumb ? <CircularProgress size={24} /> : <CloudUpload fontSize="large" />}
                                            <Typography variant="caption" mt={1}>
                                                Upload Main Cover
                                            </Typography>
                                            <input type="file" hidden accept="image/*" onChange={handleThumbnailUpload} />
                                        </Button>
                                    )}
                                </Box>
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #eee' }} elevation={0}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">Visible to Public</Typography>
                                    <Switch
                                        checked={formik.values.available}
                                        onChange={formik.handleChange}
                                        name="available"
                                        color="success"
                                    />
                                </Stack>
                            </Paper>

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={isSubmitting || uploadingThumb || uploadingImages}
                                sx={{ py: 2, borderRadius: 3 }}
                            >
                                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Publish Product'}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default VendorCreateProduct;
